import React, { useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faArrowUp, faArrowDown, faTrash, faTimes } from '@fortawesome/free-solid-svg-icons'
import ChartCard from '../dashboard/chart-card'
import { REPORT_CHART_ADAPTER } from './chart-adapter'
import { chartTypeNeedsDataSource } from 'charts/chart-type-registry'
import { datasetSourcePath } from 'helpers/utils'

// A report chart is bound to one linked dataset (View). Resolve its section so the chart
// config modal can load the right columns/analytics.
function sectionIdFor(datasets, datasetId) {
  return datasets.find((d) => d.id === datasetId)?.section_id || null
}

function datasetLabel(dataset) {
  return `${dataset.display_label || dataset.dashboard_name}${
    dataset.section ? ` (${dataset.section.abbreviation})` : ''
  }`
}

function ReportGridRow({
  gridRow,
  reportId,
  datasets = [],
  reportGroupingId = null,
  onMoveUp,
  onMoveDown,
  onDeleteRow,
  isFirst,
  isLast,
  canEdit = false,
  isPreviewMode = false,
  selectedChartId = null,
  onSelectChart = null,
  isRowSelected = false,
  onSelectRow = null,
  onDeleteColumn = null,
  selectedEmptySlot = null,
  onSelectEmptySlot = null
}) {
  const [pendingChart, setPendingChart] = useState(null)
  const [choosingPosition, setChoosingPosition] = useState(null)
  const [chosenDatasetId, setChosenDatasetId] = useState(datasets[0]?.id || '')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const { column_count, report_charts = [] } = gridRow

  // A component has to be bound to one of the report's linked Views, so an empty slot can only
  // offer "Add Component" once the report has at least one data source.
  const hasDatasets = datasets.length > 0

  // What the chart options modal offers when a component needs its data source (re)pointed.
  const dataSourceOptions = datasets.map((dataset) => ({
    id: dataset.id,
    label: datasetLabel(dataset),
    sectionId: dataset.section_id,
    // Chart filters reach the View's custom columns, and a sample takes none.
    dashboardId: dataset.dashboard_id,
    isSample: dataset.is_sample
  }))

  const columns = Array.from({ length: column_count }, (_, index) => {
    const chart = report_charts.find((c) => c.grid_column_position === index)
    return { position: index, chart }
  })

  const colSize = 12 / column_count

  const startChoosing = (position) => {
    if (datasets.length === 1) {
      setPendingChart({ position, datasetId: datasets[0].id })
      return
    }
    setChosenDatasetId(datasets[0]?.id || '')
    setChoosingPosition(position)
  }

  const confirmDataset = () => {
    if (!chosenDatasetId) return
    setPendingChart({ position: choosingPosition, datasetId: chosenDatasetId })
    setChoosingPosition(null)
  }

  const closePending = () => setPendingChart(null)

  const handleSelectRow = (e, position) => {
    e.stopPropagation()
    onSelectChart?.(null)
    onSelectRow?.(gridRow.id)
    onSelectEmptySlot?.({ rowId: gridRow.id, position })
  }

  const handleDeleteConfirm = (e) => {
    e.stopPropagation()
    onDeleteRow?.(gridRow)
    setConfirmingDelete(false)
  }

  return (
    <div className="mb-4" style={{ position: 'relative' }}>
      {isRowSelected && canEdit && !isPreviewMode && (
        <div
          style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 20, display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          onClick={(e) => e.stopPropagation()}
        >
          {confirmingDelete ? (
            <>
              <span className="small text-muted me-1">Delete this row?</span>
              <Button size="sm" variant="danger" onClick={handleDeleteConfirm}>Yes, delete</Button>
              <Button size="sm" variant="light" className="border" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="light"
                className="border"
                disabled={isFirst}
                onClick={() => onMoveUp?.(gridRow)}
                title="Move row up"
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </Button>
              <Button
                size="sm"
                variant="light"
                className="border"
                disabled={isLast}
                onClick={() => onMoveDown?.(gridRow)}
                title="Move row down"
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </Button>
              <Button
                size="sm"
                variant="light"
                className="border text-danger"
                onClick={() => setConfirmingDelete(true)}
                title="Delete row"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </>
          )}
        </div>
      )}

      <Row>
        {columns.map(({ position, chart }) => {
          const boundDataset = chart?.report_dataset_id
            ? datasets.find((d) => d.id === chart.report_dataset_id)
            : null
          // A data-bound chart with no dataset behind it is orphaned — its source View was
          // removed from the report. Removing a dataset nullifies report_dataset_id (ReportDataset
          // declares dependent: :nullify), so the id being absent is the common case; a dangling
          // id that no longer resolves counts too. Flag it so the card/options offer a reconnect
          // instead of erroring.
          const dataSourceMissing =
            !!chart && chartTypeNeedsDataSource(chart.chart_type, chart.params?.text_content) && !boundDataset
          const dataSourceLabel = boundDataset
            ? datasetLabel(boundDataset)
            : (dataSourceMissing ? 'Removed data source' : null)
          // The source behind the chart, openable in its own tab so a filter can be fixed (a View) or
          // the reviews read (a sample) without losing the report. Empty when the source can't be
          // resolved — see datasetSourcePath — which callers treat as "not linkable".
          const dataSourceHref = datasetSourcePath(boundDataset)
          return (
          <Col key={position} md={colSize}>
            {chart ? (
              <ChartCard
                chart={chart}
                adapter={REPORT_CHART_ADAPTER}
                containerId={reportId}
                sectionId={sectionIdFor(datasets, chart.report_dataset_id) || datasets[0]?.section_id}
                params={{}}
                dataSources={dataSourceOptions}
                dataSourceId={chart.report_dataset_id}
                // A report chart has no View of its own: the grouping is the one the report
                // declares, and only failing that the one behind its bound dataset — the
                // same order the server resolves in.
                groupingId={reportGroupingId}
                groupingDashboardId={boundDataset?.dashboard_id || null}
                dataSourceLabel={dataSourceLabel}
                dataSourceHref={dataSourceHref}
                dataSourceMissing={dataSourceMissing}
                canEdit={canEdit}
                isComputed={!!chart.is_body_locked}
                staticFiguresNotice={chart.static_figures_notice}
                groupingStaleNotice={chart.grouping_stale_notice}
                isPreviewMode={isPreviewMode}
                gridRowId={gridRow.id}
                gridColumnPosition={position}
                isSelected={selectedChartId === chart?.id}
                onSelect={onSelectChart}
                onMoveUp={() => onMoveUp && onMoveUp(gridRow)}
                onMoveDown={() => onMoveDown && onMoveDown(gridRow)}
                isFirst={isFirst}
                isLast={isLast}
              />
            ) : canEdit && !isPreviewMode ? (
              <Card
                className="mb-3"
                style={{
                  position: 'relative',
                  minHeight: '200px',
                  border: `2px dashed ${selectedEmptySlot?.rowId === gridRow.id && selectedEmptySlot?.position === position ? '#ffc107' : isRowSelected ? '#adb5bd' : '#dee2e6'}`,
                  cursor: choosingPosition === position ? 'default' : 'pointer'
                }}
                onClick={choosingPosition === position ? undefined : (e) => handleSelectRow(e, position)}
              >
                {onDeleteColumn && column_count > 1 && choosingPosition !== position && (
                  <Button
                    size="sm"
                    variant="light"
                    className="border text-danger p-0 d-flex align-items-center justify-content-center"
                    style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 5, width: 28, height: 28 }}
                    title="Remove this column"
                    onClick={(e) => { e.stopPropagation(); onDeleteColumn(gridRow, position) }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
                <Card.Body className="d-flex flex-column align-items-center justify-content-center">
                  {choosingPosition === position ? (
                    <div style={{ width: '100%', maxWidth: 280 }}>
                      <Form.Label className="small text-muted">Dataset for this component</Form.Label>
                      <Form.Select
                        value={chosenDatasetId}
                        onChange={(e) => setChosenDatasetId(e.target.value)}
                        className="mb-2"
                      >
                        {datasets.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.display_label || d.dashboard_name}
                            {d.section ? ` (${d.section.abbreviation})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                      <div className="d-flex gap-2 justify-content-end">
                        <Button variant="light" size="sm" onClick={(e) => { e.stopPropagation(); setChoosingPosition(null) }}>
                          Cancel
                        </Button>
                        <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); confirmDataset() }} disabled={!chosenDatasetId}>
                          Continue
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Button
                        variant="outline-primary"
                        onClick={(e) => { e.stopPropagation(); startChoosing(position) }}
                        disabled={!hasDatasets}
                        title={hasDatasets ? 'Add a component' : 'Link a data source first'}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        <span>Add Component</span>
                      </Button>
                      {/* Every component is bound to one of the report's data sources, so with none
                          linked there is nothing to build against. Say that in the slot rather than
                          leaving a greyed-out button whose only explanation is a hover title. */}
                      {!hasDatasets && (
                        <div className="small text-muted text-center mt-2" style={{ maxWidth: 280 }}>
                          This report has no data sources yet. Add one with{' '}
                          <span className="fw-semibold">Data sources</span> at the top of the page,
                          then you can add components.
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <div style={{ minHeight: '200px' }} />
            )}
          </Col>
          )
        })}
      </Row>

      {pendingChart && canEdit && !isPreviewMode && (
        <ChartCard
          chart={null}
          adapter={REPORT_CHART_ADAPTER}
          containerId={reportId}
          sectionId={sectionIdFor(datasets, pendingChart.datasetId)}
          params={{}}
          dataSources={dataSourceOptions}
          dataSourceId={pendingChart.datasetId}
          onSaved={closePending}
          autoOpenModal={true}
          onCancel={closePending}
          gridRowId={gridRow.id}
          gridColumnPosition={pendingChart.position}
          canEdit={canEdit}
          isPreviewMode={isPreviewMode}
        />
      )}
    </div>
  )
}

export default ReportGridRow
